import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as moment from 'moment';
import { DialogBase, OrderCustomerModel, RentalContractInfo, TerminationOrderInfo, TerminationRowInfo, TerminationType, ContractExtensionInfo, ContractStatus } from 't4core';
import { NewOrderDialog } from '../neworder/neworder';



@Component({
  selector: 'TerminationDialog',
  templateUrl: 'TerminationDialog.html',
  styleUrls: ['TerminationDialog.css']
})
export class TerminationDialog extends DialogBase implements OnInit {

  public order: TerminationOrderInfo;
  public rows: TerminationRowInfo[] = [];
  public contractExtensions: ContractExtensionInfo[] = [];
  public canEdit: boolean = true;
  public minExtendDate: moment.Moment = moment().startOf('day');
  public maxExtendDate: moment.Moment = moment().add(10, 'years');
  public terminationDate: moment.Moment;
  public isLoading: boolean = false;
  public selectedCustomer: OrderCustomerModel;
  public isBlocked: boolean;

  public updatingResidualValue: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<TerminationDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super("TerminationDialog");
  }

  public async ngOnInit() {
    this.order = new TerminationOrderInfo();
    this.isLoading = true;
    if (this.data.TerminationId) {
      await this.loadOrder();
    } else if (this.data.TerminationRows) {
      this.order.TerminationType = 1;

      for (let row of (this.data.TerminationRows as RentalContractInfo[])) {
        var tmp = new TerminationRowInfo();
        tmp.TrailerId = row.Id;
        tmp.Trailer = row;
        this.rows.push(tmp);
      }

      if (this.rows.length > 0) {
        this.order.TerminateAt = this.rows.sort((a, b) => {
          if (a.Trailer.OriginalEndDate < b.Trailer.OriginalEndDate) return -1;
          return 1;
        })[0].Trailer.OriginalEndDate;
      }
    }

    if (this.order.TerminateAt) {
      this.order.ExtendAt = this.order.ExtendAt ?? this.order.TerminateAt.clone().add(3, 'months');
      this.minExtendDate = this.order.TerminateAt;
      this.maxExtendDate = this.order.TerminateAt.clone().add(6, 'months');
    }

    if (this.rows.length > 0) {
      this.terminationDate = this.rows.sort((a, b) => {
        if (a.Trailer.OriginalEndDate < b.Trailer.OriginalEndDate) return -1;
        return 1;
      })[0].Trailer.OriginalEndDate;

      this.minExtendDate = this.terminationDate;
      this.maxExtendDate = this.terminationDate.clone().add(6, 'months');

      // 24R can be extended 24m
      if (this.rows.filter(x => !x.IsCancelled && x.Trailer.Customer != null).some(x => x.Trailer.Customer.Chain == "24R") || this.Settings.getUser().Username == "Josefine" || this.Settings.getUser().Username == "Björn") {
        this.maxExtendDate = this.terminationDate.clone().add(24, 'months');
      }
    }

    if (this.data.Comment) {
      this.order.Comment = this.data.Comment;
    }

    if (this.data.Insurance) {
      this.order.Insurance = this.data.Insurance;
    }

    if (this.data.TerminationType) {
      this.order.TerminationType = this.data.TerminationType;
    }

    if (this.data.TerminateAt) {
      this.order.TerminateAt = this.data.TerminateAt;
    }
    if (this.data.ContractId) {
      this.contractExtensions = await this.Api.get<any[]>("/TurboOrder/GetPendingContractExtensions", { contractId: this.data.ContractId });
    }
    if (this.data.TransferOwnership) {
      this.order.TerminationType = 5;
      this.order.IsTransferFullOwnership = true;
    }

    this.updateTerminationDate(this.order.TerminateAt);
    this.applySellInvoiceDateRules();
    this.updateResidualValues();

    if (this.order.TerminationType == TerminationType.Sell && !this.order.CustomerId) {
      await this.applyDefaultSellCustomer();
    }

    this.isLoading = false;

  }

  private async loadOrder() {
    this.order = await this.Api.get<TerminationOrderInfo>("/TurboOrder/GetTermination", { terminationId: this.data.TerminationId });
    this.rows = this.order.Rows;
    this.selectedCustomer = this.order.Customer;

    if ( this.selectedCustomer.IsBlocked) {
      this.isBlocked = true;
      this.UI.alert("Turbo.Core.SetTranslation.ErrorMsgTitleBlockedWarning", "Warning", "Turbo.Core.SetTranslation.ErrorMsgBlockedBlockedCustomer3", "Customer is blocked");
    }

    this.canEdit = !this.rows.some(x => x.IsProcessed || x.AnnataId != null);
  }

  public selectCustomer() {
    let dialog2 = this.Dialog.open(NewOrderDialog, { data: { hidePricelist: true, requireExisting: true } });
    dialog2.afterClosed().toPromise().then((result) => {
      if (result?.selectedCustomer) {
        this.applySelectedCustomer(result.selectedCustomer);
      }
    });
  }

  private applySelectedCustomer(customer: OrderCustomerModel) {
    this.selectedCustomer = customer;
    this.order.CustomerId = customer.Id;

    this.order.InvoiceName = customer.InvoiceName;
    this.order.InvoiceAddress = customer.InvoiceAddress;
    this.order.InvoiceZipCode = customer.InvoiceZipCode;
    this.order.InvoiceCity = customer.InvoiceCity;

    this.order.DeliveryName = customer.DeliveryAddressCustomerName;
    this.order.DeliveryAddress = customer.DeliveryAddress;
    this.order.DeliveryZipCode = customer.DeliveryZipCode;
    this.order.DeliveryCity = customer.DeliveryCity;

    this.isBlocked = !!customer.IsBlocked;
    if (this.isBlocked) {
      this.UI.alert("Turbo.Core.SetTranslation.ErrorMsgTitleBlockedWarning", "Warning", "Turbo.Core.SetTranslation.ErrorMsgBlockedBlockedCustomer3", "Customer is blocked");
    }
  }

  private async applyDefaultSellCustomer() {
    const sourceCustomerModel = this.rows.find(x => x.Trailer?.Customer)?.Trailer?.Customer as OrderCustomerModel | undefined;
    if (!sourceCustomerModel) return;

    const usedPartnerId = (sourceCustomerModel as any).UsedPartner;
    if (usedPartnerId) {
      try {
        const usedPartner = await this.Api.get<OrderCustomerModel>("TurboOrder/GetCustomer", { id: usedPartnerId });
        if (usedPartner) {
          this.applySelectedCustomer(usedPartner);
          return;
        }
      } catch {
      }
    }

    this.applySelectedCustomer(sourceCustomerModel);
  }

  public updateTerminationDate(value: moment.Moment) {
    var val = value.clone();
    if (val.day() > 1) val = value.clone().add(1, 'month').startOf('month');

    this.order.TerminateAt = value;

    if (this.order.TerminationType == TerminationType.Sell) {
      this.applySellInvoiceDateRules(true);
    }
    else if (this.order.Id <= 0) {
      this.order.InvoiceAt = value.clone().add(-7, 'day');

      if (this.order.InvoiceAt < moment().startOf('day')) {
        this.order.InvoiceAt = moment().startOf('day');
      }
    }

    this.updateResidualValues();
  }

  private applySellInvoiceDateRules(force: boolean = false) {
    if (this.order.TerminationType != TerminationType.Sell) return;
    if (!force && this.order.InvoiceAt) return;

    var countryRef = this.getCountryRef();
    var terminationDate = this.order.TerminateAt ? this.order.TerminateAt.clone().startOf('day') : moment().startOf('day');

    if (this.order.TerminateAt < moment().startOf('day')) {
      this.order.InvoiceAt = moment().startOf('day');
      return;
    }

    if (countryRef.toLowerCase() == 'denmark') {
      this.order.InvoiceAt = terminationDate.clone().add(1, 'month').date(5);
      return;
    }

    var treatedTerminationDate = terminationDate.clone().startOf('month');
    if (terminationDate.date() > 1) {
      treatedTerminationDate = treatedTerminationDate.add(1, 'month');
    }

    this.order.InvoiceAt = treatedTerminationDate.clone().add(-1, 'month').date(25);
  }

  private getCountryRef(): string {
    var countryRef = (this.data.countryRef || this.data.CountryRef || '').toString().trim().toLowerCase();
    
    if (countryRef) return countryRef;
  }

  public updatePrice(row: TerminationRowInfo, price: number) {
    row.Price = price;
    row.LowMargin = (row.Price < row.Trailer.ResidualValue * 1.1);
  }

  private async updateResidualValues() {
    this.updatingResidualValue = true;
    for (var i of this.rows) {
      try {
        i.Trailer.ResidualValue = await this.Api.get<number>("/TurboOrder/CalculateResidualValue", { contractId: i.Trailer.Id, terminationDate: this.order.TerminateAt });
      } catch (ex) {
        i.Trailer.ResidualValue = -1;
      }
    }

    this.updatingResidualValue = false;
  }

  public async cancelRow(row: TerminationRowInfo) {    
    var x = await this.UI.confirm('TO.TerminationDialog.headConfirmCancel', 'Confirm cancellation', 'TO.TerminationDialog.msgConfirmCancel', 'Are you sure you want to cancel the termination of this order?');
    if (!x) return;

    if (row.Id) {
      this.isLoading = true;
      if (await this.Api.get<boolean>("/TurboOrder/CancelTermination", { rowId: row.Id })) {
        row.CancelledAt = moment();
        row.CancelledBy = this.Settings.getUser().Username;
        row.IsCancelled = true;
      } else {
        this.UI.confirm('TO.TerminationDialog.headCancelFailed', 'Cancellation failed', 'TO.TerminationDialog.msgCancelFailed', 'Row could not be cancelled. Please contact your local pub for mourning...');
      }
      this.isLoading = false;
    } else {
      this.rows.splice(this.rows.indexOf(row), 1);
      if (this.rows.length <= 0) this.close();
    }
    
  }

  public async save() {
    if (!this.order.Insurance && this.order.TerminationType != TerminationType.ChangeOwner && this.rows.some(x => this.order.TerminateAt.clone().add(6, 'month') < x.Trailer.OriginalEndDate)) {
      if (await this.UI.confirm('TO.TerminationDialog.headIsInsuranceCase', 'Försäkringsfall?', 'TO.TerminationDialog.msgIsInsuranceCase', 'Är detta ett försäkringsfall?')) {
        this.order.Insurance = true;
        if (this.order.TerminationType != 3 && this.order.TerminationType != 6) {
          this.order.TerminationType = 6;
          return;
        }
      }
    }

    if (this.order.TerminationType == TerminationType.ChangeOwner && this.rows.some(x => this.order.TerminateAt.clone() >= x.Trailer.OriginalEndDate)) {
      this.UI.alert('TO.TerminationDialog.headInvalidTerminationDate', 'Invalid termination date', 'TO.TerminationDialog.msgInvalidTerminationDate', 'Termination date must be atleast one month before the current offrent date for the contracts.');
      return;
    }

    if (this.order.TerminationType == TerminationType.ChangeOwner && this.rows.some(x => this.order.CustomerId == x.Trailer.CustomerId)) {
      this.UI.alert('TO.TerminationDialog.headSameCustomer', 'Error', 'TO.TerminationDialog.msgSameCustomer', 'You cannot change owner to the same customer the trailers are already registered on.');
      return;
    }

    // Validate dates
    if (this.order.TerminateAt < moment().startOf("day")) {
      var x = await this.UI.confirm('TO.TerminationDialog.confirmLateTerminationHeadline', 'Late termination?', 'TO.TerminationDialog.confirmLateTerminationMessage', 'The termination date has already passed. Is this correct?');
      if (!x) return;
    }

    if (this.order.TerminateAt > moment().add(6, 'month')) {
      var x = await this.UI.confirm('TO.TerminationDialog.confirmEarlyTerminationHeadline', 'Early termination?', 'TO.TerminationDialog.confirmEarlyTerminationMessage', 'The termination date is more than 6 month in the future. Is this correct?');
      if (!x) return;
    }

    if (this.order.TerminationType == TerminationType.Sell) {
      // Customer exists
      if (!this.order.CustomerId) {
        this.UI.alert('TO.TerminationDialog.headMissingCustomer', 'Customer is missing', 'TO.TerminationDialog.msgMissingCustomer', 'You have to specify a customer for this sale.');
        return;
      }

      if (this.selectedCustomer && this.selectedCustomer.IsBlocked) {
        this.UI.alert('TO.TerminationDialog.headSelectedCustomerBlocked', 'Customer is blocked', 'TO.TerminationDialog.msgSelectedCustomerBlocked', 'The selected customer is blocked. You cannot sell trailer to customer that is blocked in the system.');
        return;
      }

      // Sales price on all lines
      if (this.rows.filter(x => !x.IsCancelled).some(x => !x.Price || x.Price <= 0)) {
        this.UI.alert('TO.TerminationDialog.headMissingPrice', 'Price is missing', 'TO.TerminationDialog.msgMissingPrice', 'There is no sales price for one or more trailers.')
        return;
      }

      // Check margin
      if (this.rows.filter(x => !x.IsCancelled).some(x => x.LowMargin)) {
        var x = await this.UI.confirm('TO.TerminationDialog.headLowMargin', 'Low margin!', 'TO.TerminationDialog.msgLowMissing', 'You are selling a trailer with low margin. Do you want to continue?');
        if(!x) return;
      }

      // Invoice address
      if (!this.order.InvoiceName || !this.order.InvoiceAddress || !this.order.InvoiceZipCode || !this.order.InvoiceCity) {
        this.UI.alert('TO.TerminationDialog.headMissingInvoiceAddress', 'Invoice address is missing', 'TO.TerminationDialog.msgMissingInvoiceAddress', 'You have to specify an invoice address');
        return;
      }
    }
    if (this.order.TerminationType == TerminationType.Extend) {
      if (this.order.ExtendAt < moment().startOf('day')) {
        this.UI.alert('TO.TerminationDialog.headMissingExtendDate', 'Extend date is missing', 'TO.TerminationDialog.msgMissingExtendDate', 'You have to specify the extend date');
        return;
      }

      const tooShort = this.rows.filter(x => !x.IsCancelled && x.Trailer && x.Trailer.OffRentDate)
        .some(x => this.order.ExtendAt.clone().startOf('day').diff(moment(x.Trailer.OffRentDate).startOf('day'), 'days') <= 30);
      if (tooShort) {
        this.UI.alert('TO.TerminationDialog.headExtensionTooShort', 'Extension too short', 'TO.TerminationDialog.msgExtensionTooShort', 'The extension must be more than 30 days after the current end date.');
        return;
      }

      var x = await this.UI.confirm("TO.TerminationDialog.headConfirmExtend", "Confirm extension", "TO.TerminationDialog.msgConfirmExtend", "Do you want to extend {0} contracts to {1} ?", null, [this.rows.length, this.order.ExtendAt.format("YYYY-MM-DD")]);
      if (!x) return;
    }
    else {
      if (this.order.TerminateAt > this.terminationDate && this.order.TerminationType == TerminationType.Sell) {
        var x = await this.UI.confirm("TO.TerminationDialog.headConfirmExtend", "Confirm extension", "TO.TerminationDialog.msgConfirmExtendSell", "Do you want to extend {0} contracts to {1} and sell on {2} ?", null, [this.rows.length, this.order.TerminateAt.format("YYYY-MM-DD"), this.order.InvoiceAt.format("YYYY-MM-DD")]);
        if (!x) return;
      }
      else if (this.order.TerminateAt > this.terminationDate) {
        var x = await this.UI.confirm("TO.TerminationDialog.headConfirmExtend", "Confirm extension", "TO.TerminationDialog.msgConfirmExtendOthers", "Do you want to extend {0} contracts to {1} and perform {2} ?", null, [this.rows.length, this.order.TerminateAt.format("YYYY-MM-DD"), TerminationType[this.order.TerminationType]]);
        if (!x) return;
      }
    }

    if (this.order.TerminateAt > this.terminationDate.clone().add(6, 'month')) {
      this.UI.alert('TO.TerminationDialog.headExtensionLimit', 'Extension exceed limt', 'TO.TerminationDialog.msgExtensionLimit', 'The extension date should not be more than 6 months from the termination date.');
        return;
    }

    if ((this.order.TerminationType == TerminationType.Extend || this.order.TerminationType == TerminationType.Sell) && (this.order.TerminateAt > this.terminationDate)) {
      if (this.rows.filter(x => !x.IsCancelled && x.Trailer.DeletedDt).some(x => x.Trailer.DeletedDt < moment().subtract(3, 'months'))) {
        this.UI.alert('TO.TerminationDialog.headExtensionLimitDeleted', 'Extension period expired', 'TO.TerminationDialog.msgExtensionLimitDeleted', 'The extension period is expired for one or more trailers.')
        return;
      }
    }

    if (this.order.TerminationType == TerminationType.Transport) {
      // Invoice address
      if (!this.order.DeliveryName || !this.order.DeliveryAddress || !this.order.DeliveryZipCode || !this.order.DeliveryCity) {
        this.UI.alert('TO.TerminationDialog.headMissingDeliveryAddress', 'Delivery address is missing', 'TO.TerminationDialog.msgMissingDeliveryAddress', 'You have to specify a delivery address');
        return;
      }
    }


    if (this.order.Insurance) {
      if (!this.order.Comment) {
        this.order.Comment = await this.UI.prompt('TO.TerminationDialog.headNoInsuranceComment', 'Comment for insurance case is missing', 'TO.TerminationDialog.msgNoInsuranceComment', 'Do you wich to add a descriptive comment?')
      }
    }

    this.order.Rows = this.rows.filter(x => !x.IsCancelled);

    try {
      this.isLoading = true;
      var token = this.UI.beginLoading('TO.ActiveContracts.loadSaving', 'Completing termination order', null);
      await this.Api.post<number[]>("/TurboOrder/SaveTermination", this.order);

      if (this.order.TerminationType == TerminationType.Extend) {
        this.UI.alert('TO.ActiveContracts.msgSavedExtensionHeadline', 'Extension order is registered', 'TO.ActiveContracts.msgSaveExtensionMessage', 'Your extension order has been registered. Have a nice day now!')
      }
      else {
        this.UI.alert('TO.ActiveContracts.msgSavedHeadline', 'Order is registered', 'TO.ActiveContracts.msgSaveMessage', 'Your order has been registered. An order document has been generated and will be sent to both you and your rental administrator via email shortly. Have a nice day now!')
      }
      this.UI.loaderCompleted(token);
      this.close();

      //Termination type transport set vehicles last known location
      if (this.order.TerminationType == TerminationType.Transport) {
        for (let row of this.rows) {
          await this.Api.get<boolean>("TurboOrder/MoveVehicle", { vehicleId: row.Trailer.VehicleId, customerId: this.order.CustomerId, positionDate: this.order.TerminateAt });
        }

      }
      this.isLoading = false;
    } catch (e) {
      this.UI.loaderCompleted(token);
      this.UI.alert('TO.ActiveContracts.msgSaveFailedHeadline', 'Termination failed', 'TO.ActiveContracts.msgSaveFailedMessage', 'I have failed to save the termination order. Please try again.');
      this.isLoading = false;
    }
  }


  public setToCurrentDate() {
    this.updateTerminationDate(moment().startOf('day'));
  }

  public close() {
    this.dialogRef.close();
  }

  public async terminationTypeChanged(value: TerminationType) {
    if (value == TerminationType.Extend && this.terminationDate) {
      this.order.TerminateAt = this.terminationDate;
      return;
    }

    if (value == TerminationType.Sell) {
      this.applySellInvoiceDateRules(true);
    }

    if (value == TerminationType.Sell && !this.order.CustomerId) {
      await this.applyDefaultSellCustomer();
    }
  }
}
